import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLocation,
  useParams,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';

/* ========================== small utils ================== */
const safeJsonParse = (str) => {
  try {
    if (!str || typeof str !== 'string') return {};
    return JSON.parse(str);
  } catch {
    try {
      return JSON.parse(String(str).replace(/'/g, '"').replace(/=>/g, ':'));
    } catch {
      return {};
    }
  }
};
const prefixIfRelative = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://app.yaper.co${url.startsWith('/') ? '' : '/'}${url}`;
};
const toNumber = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

/* ================== styles for BLOG (desktop-only) ================== */
const stylesBase = {
  pageContainer: {
    backgroundColor: '#f6f7fb',
    fontFamily: 'Inter, Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    minHeight: '100vh',
    maxHeight: '100vh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    color: '#111827',
    paddingBottom: '60px',
  },
  inner: { maxWidth: '1200px', margin: '0 auto' },

  hero: {
    position: 'relative',
    width: '100%',
    height: '520px',
    maxWidth: '820px',
    margin: '24px auto',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(13, 26, 72, 0.08)',
    backgroundColor: '#0b0f1a',
  },
  heroImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  heroShade: {
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0) 70%)',
    pointerEvents: 'none',
  },
  heroOverlay: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    padding: '28px 36px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'flex-end',
    pointerEvents: 'none',
  },
  heroContent: {
    color: '#fff',
    pointerEvents: 'auto',
    background:
      'linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
    padding: '16px 18px',
    borderRadius: '10px',
    maxWidth: '60%',
    boxShadow: '0 6px 18px rgba(2,6,23,0.18)',
  },
  heroTitle: {
    fontSize: '1.6rem',
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.2,
  },
  heroMeta: { marginTop: 6, fontSize: 13, color: '#e6eefc' },

  contentArea: {
    display: 'flex',
    gap: 28,
    padding: '36px 16px',
    maxWidth: '1200px',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  contentCard: {
    flex: 3,
    background: '#fff',
    padding: 36,
    borderRadius: 12,
    boxShadow: '0 6px 18px rgba(13, 26, 72, 0.04)',
  },
  section: { marginTop: 16 },
  sectionTitle: { marginTop: 0 },

  article: { fontSize: 16, color: '#1f2937', lineHeight: 1.78 },

  sidebar: {
    flex: 1,
    maxWidth: 320,
    position: 'sticky',
    top: 100,
    alignSelf: 'flex-start',
    height: 'fit-content',
  },
  sidebarTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 12,
    color: '#0f1724',
  },
  tocList: { listStyle: 'none', padding: 0, margin: 0 },
  tocItem: { marginBottom: 10 },
  tocLink: {
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    color: '#374151',
    fontSize: 14,
    textAlign: 'left',
    cursor: 'pointer',
    padding: '8px 12px',
    display: 'block',
    width: '100%',
    borderRadius: 8,
  },
  tocLinkActive: {
    color: '#0f4dc2',
    fontWeight: 700,
    backgroundColor: 'rgba(14,77,194,0.06)',
    borderLeft: '3px solid #0f4dc2',
    paddingLeft: 10,
  },

  tableWrapper: {
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '1rem',
    marginTop: '1rem',
  },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' },
  td: { padding: 8, border: '1px solid #e5e7eb' },
};

const styles = {
  pageWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: 'Titillium Web, sans-serif',
    background: '#f0f6ff',
    padding: '2rem',
    boxSizing: 'border-box',
  },
  backButton: {
    position: 'absolute',
    top: '1.5rem',
    left: '2rem',
    background: 'none',
    border: 'none',
    color: '#1e3a8a',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  mainContent: {
    flex: 2,
    background: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    marginRight: '2rem',
    boxSizing: 'border-box',
    overflow: 'hidden',
    minWidth: 0,
  },
  blueCardSection: {
    width: '100%',
    background: 'linear-gradient(to right, #003366, #0059b3)',
    borderRadius: '12px',
    padding: '1.25rem 1.5rem',
    marginBottom: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    gap: '1rem',
    overflow: 'hidden',
    boxSizing: 'border-box',
    minHeight: 110,
    flexWrap: 'nowrap',
  },
  cardImageWrap: {
    flex: '0 0 220px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    borderRadius: 12,
    padding: 8,
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
    height: 110,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  cardTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    margin: 0,
    color: '#fff',
    lineHeight: 1.1,
  },
  titleContainer: {
    flex: '1 1 auto',
    minWidth: 0,
    paddingLeft: 12,
    paddingRight: 12,
    boxSizing: 'border-box',
  },
  ratingCol: { flex: '0 0 90px', textAlign: 'right', minWidth: 90 },
  ratingValue: { fontSize: '2rem', fontWeight: 'bold', color: '#fff' },
  ratingLabel: { fontSize: '0.9rem', color: '#ffd700' },
  tabsBar: {
    display: 'flex',
    marginTop: '1rem',
    borderBottom: '1px solid #ddd',
  },
  tabItem: {
    marginRight: '1.5rem',
    paddingBottom: '0.5rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  tabContentBox: { padding: '1.5rem 0' },
  contentText: { color: '#444', fontSize: '1rem', lineHeight: 1.6 },
  list: {
    paddingLeft: '1.5rem',
    fontSize: '1rem',
    color: '#444',
    lineHeight: 1.6,
  },
  listItem: { marginBottom: '0.5rem' },
  ratesContainer: { display: 'flex', justifyContent: 'space-between' },
  rateItem: { fontSize: '1rem', color: '#444' },
  sidebar: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
  applyBox: {
    background: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    textAlign: 'center',
  },
  applyTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700 },
  applyDesc: { fontSize: '0.9rem', color: '#444', margin: '0.5rem 0' },
  applyButton: {
    background: '#1e3a8a',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  applyNote: { fontSize: '0.75rem', color: '#666', marginTop: '1rem' },
  videoBox: {
    overflow: 'hidden',
    borderRadius: '8px',
    background: '#fff',
    marginTop: 8,
  },
  detailsContainer: { marginTop: '2rem' },
};

/* ===================== INLINE TABLE PARSER (robust) ===================== */
const splitCsvRow = (row) => {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '"') {
      if (inQ && row[i + 1] === '"') {
        cur += '"';
        i++;
      } else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out.map((c) => c.replace(/^"(.*)"$/s, '$1').trim());
};

const buildTableElement = (doc, title, rows) => {
  const wrapper = doc.createElement('div');
  wrapper.style.border = '1px solid #e5e7eb';
  wrapper.style.borderRadius = '12px';
  wrapper.style.padding = '1rem';
  wrapper.style.marginTop = '1rem';

  const h = doc.createElement('h4');
  h.style.marginTop = '0';
  h.textContent = title || 'Table';
  wrapper.appendChild(h);

  const tbl = doc.createElement('table');
  tbl.style.width = '100%';
  tbl.style.borderCollapse = 'collapse';
  tbl.style.marginTop = '0.5rem';
  const tbody = doc.createElement('tbody');

  rows.forEach((r, rIdx) => {
    const tr = doc.createElement('tr');
    r.forEach((c) => {
      const cell = doc.createElement(rIdx === 0 ? 'th' : 'td');
      cell.style.padding = '8px';
      cell.style.border = '1px solid #e5e7eb';
      if (rIdx === 0) cell.style.fontWeight = '700';
      cell.textContent = c;
      tr.appendChild(cell);
    });
    tbody.appendChild(tr);
  });

  tbl.appendChild(tbody);
  wrapper.appendChild(tbl);
  return wrapper;
};

const isBlock = (el) =>
  el &&
  el.nodeType === 1 &&
  ['P', 'LI', 'DIV', 'PRE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(
    el.tagName
  );
const nearestBlock = (node) => {
  let n = node;
  while (n && n.nodeType === 1 && !isBlock(n)) n = n.parentElement;
  return isBlock(n) ? n : null;
};
const nextNonEmptyBlock = (el) => {
  let cur = el;
  while (cur && cur.nextSibling) {
    cur = cur.nextSibling;
    if (cur.nodeType === 3 && !cur.textContent.trim()) continue;
    if (cur.nodeType === 1 && isBlock(cur)) {
      const txt = (cur.textContent || '').replace(/\u00A0/g, ' ').trim();
      if (txt) return cur;
    }
  }
  return null;
};
const splitIntoRowStrings = (raw) => {
  const cleaned = (raw || '').replace(/\u00A0/g, ' ').trim();
  if (!cleaned) return [];
  if (cleaned.includes(';'))
    return cleaned
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);
  return cleaned
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
};

// tolerant marker
const startMarkerRegex =
  /^\s*(?:•\s*)?\{\s*table[_-]?[^}]*\}\s*(?:-|–|—)\s*(['"“”])([\s\S]*?)\1\s*$/;

const processContentHTML = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html || '', 'text/html');

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
  const toReplace = [];

  while (walker.nextNode()) {
    const t = walker.currentNode;
    const text = (t.textContent || '').replace(/\u00A0/g, ' ').trim();
    if (!text || !text.includes('{') || !text.includes('}')) continue;

    const block = nearestBlock(t.parentElement || t);
    if (!block) continue;

    const match = startMarkerRegex.exec(text);
    if (match) {
      const title = (match[2] || '').trim() || 'Table';
      toReplace.push({ block, title });
    }
  }

  toReplace.forEach(({ block, title }) => {
    const dataBlock = nextNonEmptyBlock(block);
    if (!dataBlock) return;

    const raw = dataBlock.textContent || '';
    const rowStrings = splitIntoRowStrings(raw);
    if (!rowStrings.length) return;

    const rows = rowStrings.map(splitCsvRow).filter((r) => r.length > 0);
    if (!rows.length) return;

    const tableEl = buildTableElement(doc, title, rows);

    dataBlock.replaceWith(tableEl);
    block.remove();
  });

  const headings = [];
  const all = doc.body.querySelectorAll('h1,h2,h3,h4,h5,h6');
  all.forEach((el, idx) => {
    const id = `section-${idx}`;
    el.id = id;
    headings.push({
      id,
      text: el.textContent || `Section ${idx + 1}`,
      level: Number(el.tagName.slice(1)),
    });
  });

  return { html: doc.body.innerHTML, headings };
};

const processOnlyHtml = (html) => {
  const s = String(html || '');
  const looksPlain = !/[<][a-z!/]/i.test(s);
  const prepared = looksPlain
    ? s
        .split(/\n{2,}/)
        .map((p) => `<p>${p.trim()}</p>`)
        .join('\n')
    : s;

  return processContentHTML(prepared).html;
};

/* ======================= YOUTUBE helpers ======================= */
const extractYouTubeId = (input = '') => {
  const raw = String(input || '').trim();
  if (!raw) return '';
  // if already an embed url with video id
  const embedMatch = raw.match(/\/embed\/([A-Za-z0-9_-]{6,})/i);
  if (embedMatch) return embedMatch[1];
  // youtu.be short links
  const reShort = raw.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/i);
  if (reShort) return reShort[1];
  // watch?v=
  const reWatch = raw.match(/[?&]v=([A-Za-z0-9_-]{6,})/i);
  if (reWatch) return reWatch[1];
  // shorts
  const reShorts = raw.match(/\/shorts\/([A-Za-z0-9_-]{6,})/i);
  if (reShorts) return reShorts[1];
  // last path segment candidate
  const segs = raw.split('/').filter(Boolean);
  const last = segs[segs.length - 1] || '';
  if (/^[A-Za-z0-9_-]{6,}$/.test(last)) return last;
  return '';
};
const toYouTubeEmbed = (val = '') => {
  const raw = String(val || '').trim();
  if (!raw) return '';
  // If full iframe src or embed style already provided, try to extract src
  if (/<iframe/i.test(raw)) {
    try {
      const doc = new DOMParser().parseFromString(raw, 'text/html');
      const ifr = doc.querySelector('iframe');
      if (ifr && ifr.src) {
        const id = extractYouTubeId(ifr.src);
        if (id) return `https://www.youtube.com/embed/${id}`;
        return ifr.src;
      }
    } catch (e) {}
  }

  if (/youtube\.com\/embed\/[A-Za-z0-9_-]+/.test(raw)) return raw;
  const id = extractYouTubeId(raw);
  if (id) return `https://www.youtube.com/embed/${id}`;

  const urlMatch = raw.match(
    /https?:\/\/(www\.)?(youtube\.com|youtu\.be)[^\s'"]+/i
  );
  if (urlMatch) {
    const id2 = extractYouTubeId(urlMatch[0]);
    if (id2) return `https://www.youtube.com/embed/${id2}`;
    return urlMatch[0];
  }

  return '';
};

const findYouTubeInHtml = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    const iframe = doc.querySelector(
      'iframe[src*="youtube"], iframe[src*="youtu.be"]'
    );
    if (iframe && iframe.src) return iframe.src;
    const a = doc.querySelector('a[href*="youtube"], a[href*="youtu.be"]');
    if (a && a.href) return a.href;
    const text = doc.body.textContent || '';
    const re =
      /(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/i;
    const m = re.exec(text);
    if (m) {
      const cand = m[0].startsWith('http') ? m[0] : `https://${m[0]}`;
      return cand;
    }
  } catch (e) {}
  const re2 = /(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)[^\s'"]+)/i;
  const mm = re2.exec(String(html));
  return mm ? mm[1] : '';
};

/* ======================= MAIN COMPONENT ======================= */
export default function PreviewBlog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: slugParam } = useParams();
  const [searchParams] = useSearchParams();

  const [bundle, setBundle] = useState(location.state || null);
  const [loading, setLoading] = useState(!location.state);

  // layout / toc state (normal blog)
  const pageRef = useRef(null);
  const [processedHtml, setProcessedHtml] = useState('');
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState('');

  // If preview payload lacks a card image, this state will hold one resolved via cards API
  const [ccImageFromCard, setCcImageFromCard] = useState('');

  // CC tab state (moved to top-level to avoid conditional-hook error)
  const [ccActiveTab, setCcActiveTab] = useState('Card Details');

  /* ---------- fetch when opened by URL ---------- */
  useEffect(() => {
    const slug =
      slugParam || searchParams.get('slug') || searchParams.get('id');
    if (!slug) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        if (location.state) {
          const existing = location.state;
          // If the preview was opened from the editor, prefer the passed-in state so
          // newly-created CC items render as CC previews immediately (even if card image is missing).
          setBundle(existing);
          setLoading(false);
          return; // skip server fetch so preview matches editor state
        }

        const res = await fetch(
          `https://app.yaper.co/api/v1/content_pages/${slug}`
        );
        if (!res.ok) {
          const t = await res.text().catch(() => '(no body)');
          console.error(
            'content_pages fetch failed',
            res.status,
            res.statusText,
            t
          );
          if (!cancelled) setLoading(false);
          return;
        }
        const json = await res.json();

        const b = json?.data;
        if (!b || cancelled) {
          if (!cancelled) {
            console.warn('Preview fetch returned no data for slug:', slug);
            setLoading(false);
          }
          return;
        }

        const parsedMeta = b.meta ? safeJsonParse(b.meta) : {};

        const cardIdCandidate =
          b.card_id ||
          parsedMeta.card_id ||
          b.meta?.card_id ||
          parsedMeta?.card?.id ||
          b.card?.id ||
          (b.card && b.card.id) ||
          null;

        const hasCardData =
          (b.details && typeof b.details === 'string' && b.details.trim()) ||
          (Array.isArray(b.benefits) &&
            b.benefits.length > 0 &&
            typeof b.benefits[0] === 'object') ||
          (typeof b.benefits === 'string' && b.benefits.trim());

        const rawHeading = b.creditcardname || b.heading || '';
        const cleanHeading = rawHeading.replace(/^Title:\s*/i, '').trim();

        const form = {
          heading: cleanHeading, // ← add creditcardname fallback
          content: b.content && b.content.trim() ? b.content : b.details || '', // ← fallback to details
          previewImage: prefixIfRelative(
            b.preview_img || b.cardImage || b.card_image || ''
          ),
          previewText: b.previewText || b.preview_text || b.description || '',
          tags: b.tags || '',
          type: b.content_type || (hasCardData ? 'cc_blog' : 'blog'), // ← infer type
          cardImage: b.cardImage || b.card_image || '',
          youtubeLink: b.youtubeLink || b.youtube_link || '',
          youtube_link: b.youtube_link || b.youtubeLink || '',
        };

        // Merge youtube values from top-level and meta into the returned bundle
        const youtubeFromTop = (
          b.youtubeLink ||
          b.youtube_link ||
          b.youtube ||
          ''
        ).trim();

        const meta = {
          details: b.details || parsedMeta.details || '', // ← read from top-level
          overview: b.description || parsedMeta.overview || b.previewText || '',
          benefits: b.benefits || parsedMeta.benefits || '', // ← read from top-level
          fees: b.fees || parsedMeta.fees || '', // ← read from top-level
          tables: b.tables || parsedMeta.tables || null,
          card: parsedMeta.card || null,
          card_id: b.card_id || parsedMeta.card_id || cardIdCandidate || null,
          youtube_link:
            b.youtubeLink || b.youtube_link || parsedMeta.youtube_link || '',
          youtubeLink:
            b.youtubeLink || b.youtube_link || parsedMeta.youtubeLink || '',
          youtube: b.youtubeLink || parsedMeta.youtube || '',
          buttonText: parsedMeta.buttonText || '',
          bank: b.bank || '',
          rating: b.rating || 0,
        };

        // Expose youtube fields on form too (so preview can read form.youtubeLink)
        form.youtubeLink =
          youtubeFromTop ||
          parsedMeta.youtubeLink ||
          parsedMeta.youtube_link ||
          '';
        form.youtube_link =
          youtubeFromTop ||
          parsedMeta.youtube_link ||
          parsedMeta.youtubeLink ||
          '';

        const card = b.card || null;

        if (!cancelled)
          setBundle({
            content_type: form.type,
            form,
            meta,
            slug,
            card,
            card_id: cardIdCandidate,
          });
      } catch (e) {
        console.error('Preview fetch failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.state, slugParam, searchParams]);

  // If we have bundle and it's cc_blog, but still don't have an image via bundle keys, try to fetch card record by id
  useEffect(() => {
    if (!bundle) return;
    if (
      !String(bundle?.content_type || '')
        .toLowerCase()
        .includes('cc')
    )
      return;

    const explicit =
      bundle?.form?.cardImage ||
      bundle?.form?.card_image ||
      bundle?.meta?.card?.card_image ||
      bundle?.card?.card_image ||
      bundle?.card?.url;
    if (explicit) return;

    const cardId =
      bundle?.card_id || bundle?.form?.card_id || bundle?.meta?.card_id || null;
    if (!cardId) return;

    let cancelled = false;
    (async () => {
      try {
        const singleUrl = `https://app.yaper.co/api/v1/cards/${cardId}`;
        try {
          const r = await fetch(singleUrl);
          if (r.ok) {
            const jd = await r.json();
            const found = jd?.data || jd?.card || jd || null;
            if (found) {
              const cand =
                found.card_image ||
                found.image_url ||
                found.url ||
                (found.card &&
                  (found.card.card_image ||
                    found.card.image_url ||
                    found.card.url)) ||
                '';
              if (cand) {
                const pref = prefixIfRelative(cand);
                if (!cancelled) {
                  setCcImageFromCard(pref);
                  return;
                }
              }
            }
          }
        } catch (err) {
          // continue to list fallback below
        }

        let list = [];
        try {
          const r = await fetch('https://app.yaper.co/api/v1/cards/get_cards');
          if (r.ok) {
            const j = await r.json();
            list = Array.isArray(j?.cards)
              ? j.cards
              : Array.isArray(j)
                ? j
                : j?.data || [];
          }
        } catch (err) {}

        if (!list || !list.length) {
          try {
            const alt = await fetch('https://app.yaper.co/api/v1/cards');
            if (alt.ok) {
              const j = await alt.json();
              list = Array.isArray(j?.cards)
                ? j.cards
                : Array.isArray(j)
                  ? j
                  : j?.data || [];
            }
          } catch (err) {}
        }

        if (!list || !list.length) return;

        const found = list.find(
          (c) =>
            String(c.id) === String(cardId) ||
            String(c.card?.id) === String(cardId) ||
            String(c?.card_id) === String(cardId)
        );
        if (!found) return;

        const cand =
          found.card_image ||
          found.image_url ||
          found.url ||
          (found.card &&
            (found.card.card_image ||
              found.card.image_url ||
              found.card.url)) ||
          '';
        if (cand && !cancelled) {
          const pref = prefixIfRelative(cand);
          setCcImageFromCard(pref);
        }
      } catch (e) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [bundle]);

  // Process content for normal blog path
  useEffect(() => {
    if (!bundle?.form?.content || bundle?.content_type !== 'blog') {
      setProcessedHtml('');
      setToc([]);
      return;
    }
    const { html, headings } = processContentHTML(bundle.form.content);
    setProcessedHtml(html);
    setToc(headings);
    setActiveId(headings[0]?.id || '');
  }, [bundle?.form?.content, bundle?.content_type]);

  // TOC intersection
  useEffect(() => {
    if (!toc.length || !processedHtml || typeof window === 'undefined') return;
    const rootEl = pageRef.current || null;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        visible.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
        );
        const topMost = visible[0];
        if (topMost?.target?.id) setActiveId(topMost.target.id);
      },
      { root: rootEl, rootMargin: '-80px 0px -50% 0px', threshold: 0.01 }
    );

    toc.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [toc, processedHtml]);

  const scrollToId = (id) => {
    setActiveId(id);
    if (typeof window === 'undefined') return;
    const target = document.getElementById(id);
    const container = pageRef.current;
    if (target && container) {
      const yOffset = -90;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const relativeTop = targetRect.top - containerRect.top;
      const y = container.scrollTop + relativeTop + yOffset;
      container.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  /* ========================= CC-BLOG mapping ========================= */
  const isCC = String(bundle?.content_type || '')
    .toLowerCase()
    .includes('cc');

  // Build a normalized `card` object used by the new design
  const card = useMemo(() => {
    if (!isCC) return null;

    const c = bundle?.card || bundle?.meta?.card || {};

    const imageCandidates = [
      bundle?.form?.cardImage,
      bundle?.form?.card_image,
      bundle?.meta?.card?.card_image,
      bundle?.meta?.card_image,
      bundle?.card?.card_image,
      bundle?.card?.image_url,
      bundle?.card?.url,
      bundle?.form?.previewImage,
    ];

    let rawImg = '';
    for (let i = 0; i < imageCandidates.length; i++) {
      const v = imageCandidates[i];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        rawImg = String(v).trim();
        break;
      }
    }

    const finalImage = rawImg
      ? prefixIfRelative(rawImg)
      : ccImageFromCard || '';

    return {
      id: c?.id ?? bundle?.card_id ?? bundle?.meta?.card_id ?? null,
      cardImage: finalImage,
      creditcardname:
        bundle?.form?.heading ||
        c?.display_name ||
        c?.long_name ||
        c?.short_name ||
        bundle?.form?.heading ||
        'Card',
      rating: toNumber(
        c?.rating ?? bundle?.meta?.rating ?? bundle?.form?.rating ?? 0,
        0
      ),
      bank:
        c?.bank ||
        c?.bank_name ||
        bundle?.meta?.bank ||
        bundle?.form?.bankName ||
        '',
      affiliateLink:
        c?.affiliate ||
        bundle?.meta?.affiliate ||
        c?.affiliate_link ||
        bundle?.form?.affiliate ||
        '',
      buttonText:
        bundle?.meta?.buttonText || bundle?.form?.buttonText || 'APPLY NOW',
      youtubeLink:
        c?.youtube_link ||
        bundle?.meta?.youtube_link ||
        bundle?.meta?.youtubeLink ||
        bundle?.form?.youtubeLink ||
        bundle?.form?.youtube_link ||
        bundle?.form?.youtube ||
        bundle?.form?.youtubeLink ||
        '',
      overview: bundle?.meta?.overview || bundle?.form?.previewText || '',
      benefits: bundle?.meta?.benefits || '',
      fees: bundle?.meta?.fees || '',
      details: bundle?.meta?.details || '',
    };
  }, [bundle, isCC, ccImageFromCard]);

  const ccOverviewHtml = useMemo(
    () => (isCC ? processOnlyHtml(card?.overview || '') : ''),
    [isCC, card]
  );
  const ccBenefitsHtml = useMemo(
    () => (isCC ? processOnlyHtml(card?.benefits || '') : ''),
    [isCC, card]
  );
  const ccFeesHtml = useMemo(
    () => (isCC ? processOnlyHtml(card?.fees || '') : ''),
    [isCC, card]
  );
  const ccDetailsHtml = useMemo(
    () => (isCC ? processOnlyHtml(card?.details || '') : ''),
    [isCC, card]
  );

  /* ============== Robust embed detection ==============
     Strategy:
       1) collect ordered candidates (form.youtubeLink first),
       2) if candidate contains iframe/html -> extract iframe src or anchor,
       3) try to produce a youtube embed URL via toYouTubeEmbed,
       4) fallback: scan HTML fields (content/previewText/meta.details/meta.overview)
  ===================================================== */
  const embedSrc = useMemo(() => {
    if (!bundle && !card) return '';

    const ordered = [
      bundle?.form?.youtubeLink,
      bundle?.form?.youtube_link,
      bundle?.form?.video,
      bundle?.form?.video_link,
      bundle?.form?.youtube,
      bundle?.meta?.youtubeLink,
      bundle?.meta?.youtube_link,
      bundle?.meta?.youtube,
      bundle?.meta?.video,
      bundle?.card?.youtube_link,
      bundle?.card?.youtubeLink,
      card?.youtubeLink,
    ]
      .filter(Boolean)
      .map(String);

    for (const cand of ordered) {
      const fromHtml = findYouTubeInHtml(cand);
      if (fromHtml) {
        const emb = toYouTubeEmbed(fromHtml);
        if (emb) return emb;
      }
      const emb2 = toYouTubeEmbed(cand);
      if (emb2) return emb2;
    }

    const htmlFields = [
      bundle?.form?.content,
      bundle?.form?.previewText,
      bundle?.meta?.details,
      bundle?.meta?.overview,
      bundle?.meta?.benefits,
    ]
      .filter(Boolean)
      .map(String);

    for (const hf of htmlFields) {
      const found = findYouTubeInHtml(hf);
      if (found) {
        const emb = toYouTubeEmbed(found);
        if (emb) return emb;
      }
    }

    const fallbackRaw = (
      bundle?.form?.youtubeLink ||
      bundle?.meta?.youtubeLink ||
      bundle?.form?.youtube ||
      ''
    ).trim();
    if (fallbackRaw) {
      const emb = toYouTubeEmbed(fallbackRaw);
      if (emb) return emb;
    }

    return '';
  }, [bundle, card]);

  // helpful debug logs to paste here if still failing
  useEffect(() => {
    console.info('Preview bundle (debug):', bundle);
    console.info('Computed embedSrc (debug):', embedSrc);
  }, [bundle, embedSrc]);

  /* ---------------- Early returns ---------------- */
  if (loading) return <div style={{ padding: '2rem' }}>Loading preview…</div>;
  if (!bundle) {
    return (
      <div style={{ padding: '2rem' }}>
        <p>No preview data available.</p>
        <button onClick={() => navigate(-1)} style={{ padding: '8px 12px' }}>
          ← Back
        </button>
      </div>
    );
  }

  const { content_type, form } = bundle;
  const applied = stylesBase;

  /* ========================= NORMAL BLOG ========================= */
  if (content_type === 'blog') {
    return (
      <div ref={pageRef} style={applied.pageContainer} data-scroll-container>
        <div style={applied.inner}>
          <button onClick={() => navigate(-1)} style={{ margin: '12px 0' }}>
            ← Back
          </button>

          {(form.previewImage || form.heading) && (
            <div style={applied.hero}>
              {form.previewImage ? (
                <img
                  src={form.previewImage}
                  alt={form.heading || 'Preview'}
                  style={applied.heroImg}
                />
              ) : null}
              <div style={applied.heroShade} />
              <div style={applied.heroOverlay}>
                <div style={applied.heroContent}>
                  <h1 style={applied.heroTitle}>{form.heading || 'Blog'}</h1>
                  {form.tags ? (
                    <p style={applied.heroMeta}>{form.tags}</p>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          <div style={applied.contentArea}>
            <main style={applied.contentCard}>
              {form.previewText ? (
                <section style={applied.section}>
                  <h3 style={applied.sectionTitle}>Preview</h3>
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{
                      __html: processOnlyHtml(form.previewText),
                    }}
                  />
                </section>
              ) : null}

              <section style={applied.section}>
                <h3 style={applied.sectionTitle}>Content</h3>
                <div
                  className="ql-editor"
                  style={applied.article}
                  dangerouslySetInnerHTML={{ __html: processedHtml }}
                />
              </section>
            </main>

            <aside style={applied.sidebar}>
              {toc.length > 0 && (
                <>
                  <h3 style={applied.sidebarTitle}>In this article</h3>
                  <ul style={applied.tocList}>
                    {toc.map(({ id, text, level }) => (
                      <li key={id} style={applied.tocItem}>
                        <button
                          onClick={() => scrollToId(id)}
                          style={
                            id === activeId
                              ? {
                                  ...applied.tocLink,
                                  ...applied.tocLinkActive,
                                  paddingLeft: 10 + (level - 1) * 8,
                                }
                              : {
                                  ...applied.tocLink,
                                  paddingLeft: 12 + (level - 1) * 8,
                                }
                          }
                          title={text}
                        >
                          {text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  /* ========================= CC BLOG (Desktop-only) ========================= */
  if (isCC) {
    const tabs = [
      'Card Details',
      'Benefits & Features',
      'Rates & Fees',
      'Full Details',
    ];

    const renderTabContent = () => {
      switch (ccActiveTab) {
        case 'Card Details':
          return (
            <div
              className="ql-editor"
              style={styles.contentText}
              dangerouslySetInnerHTML={{
                __html: ccOverviewHtml || processOnlyHtml(form.previewText),
              }}
            />
          );
        case 'Benefits & Features':
          return ccBenefitsHtml ? (
            <div
              className="ql-editor"
              style={styles.contentText}
              dangerouslySetInnerHTML={{ __html: ccBenefitsHtml }}
            />
          ) : (
            <div style={{ color: '#666' }}>No benefits available.</div>
          );
        case 'Rates & Fees':
          return ccFeesHtml ? (
            <div
              className="ql-editor"
              style={styles.contentText}
              dangerouslySetInnerHTML={{ __html: ccFeesHtml }}
            />
          ) : (
            <div style={{ color: '#666' }}>No rates/fees available.</div>
          );
        case 'Full Details':
          return ccDetailsHtml ? (
            <div
              className="ql-editor"
              style={styles.contentText}
              dangerouslySetInnerHTML={{ __html: ccDetailsHtml }}
            />
          ) : (
            <div style={{ color: '#666' }}>No additional details.</div>
          );
        default:
          return null;
      }
    };

    return (
      <div style={styles.pageWrapper}>
        <button onClick={() => navigate(-1)} style={styles.backButton}>
          ← Back to Cards
        </button>

        <div style={styles.mainContent}>
          {/* Blue header */}
          <div style={styles.blueCardSection}>
            <div style={styles.cardImageWrap}>
              <img
                src={card.cardImage || 'https://placehold.co/176x77?text=Card'}
                alt={card.bank || card.creditcardname}
                style={styles.cardImage}
                onError={(e) => {
                  e.currentTarget.src =
                    'https://placehold.co/176x77?text=Not+Found';
                }}
              />
            </div>

            <div style={styles.titleContainer}>
              <h2 style={styles.cardTitle}>{card.creditcardname}</h2>
            </div>

            <div style={styles.ratingCol}>
              <div style={styles.ratingValue}>
                {(card.rating || 0).toFixed(1)}
              </div>
              <div style={styles.ratingLabel}>
                ★ <span style={{ color: '#fff' }}>out of 5</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={styles.tabsBar}>
            {tabs.map((tab) => (
              <div
                key={tab}
                onClick={() => setCcActiveTab(tab)}
                style={{
                  ...styles.tabItem,
                  borderBottom:
                    ccActiveTab === tab
                      ? '2px solid #1e3a8a'
                      : '2px solid transparent',
                  color: ccActiveTab === tab ? '#1e3a8a' : '#444',
                }}
              >
                {tab}
              </div>
            ))}
          </div>

          <div style={styles.tabContentBox}>{renderTabContent()}</div>
        </div>

        {/* Sidebar (desktop): only Apply box + VIDEO below it. */}
        <aside style={styles.sidebar}>
          <div style={styles.applyBox}>
            <h3 style={styles.applyTitle}>Apply for this card</h3>
            <p style={styles.applyDesc}>
              Ready to enjoy the benefits of the {card.creditcardname}? Apply
              now to get started.
            </p>
            <button
              style={styles.applyButton}
              onClick={() => {
                if (!card.affiliateLink) return;
                window.open(card.affiliateLink, '_self');
              }}
            >
              {card.buttonText || 'APPLY NOW'}
            </button>
            <p style={styles.applyNote}>
              By clicking "Apply Now", you will be redirected to the issuer’s
              secure application page.
            </p>
          </div>

          {/* Video placed intentionally below Apply Box */}
          {embedSrc ? (
            <div style={styles.videoBox}>
              <iframe
                title="Card Video"
                src={embedSrc}
                style={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  height: 'auto',
                  display: 'block',
                  border: 0,
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : null}
        </aside>
      </div>
    );
  }

  /* ========================= FALLBACK STATIC ========================= */
  const box = { maxWidth: 1100, margin: '0 auto', padding: '1.25rem' };
  const h1 = { margin: '0 0 .5rem', fontSize: '1.75rem' };
  const img = {
    maxWidth: '100%',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    display: 'block',
    marginBottom: '1rem',
  };

  return (
    <div style={box}>
      <button onClick={() => navigate(-1)} style={{ marginBottom: '1rem' }}>
        ← Back
      </button>
      <h1 style={h1}>{form.heading || 'Static Page'}</h1>

      {form.previewImage ? (
        <img
          src={form.previewImage}
          alt={form.heading || 'Preview'}
          style={img}
        />
      ) : null}

      <div
        className="ql-editor"
        dangerouslySetInnerHTML={{ __html: processOnlyHtml(form.content) }}
      />
    </div>
  );
}
